import {
  Button,
  Container,
  Form,
  Input,
  RadioGroup,
  Select,
  Stack,
  Text,
  TimeSpan,
} from "basis";
import { FormActions } from "components/FormActions/FormActions";
import { AccountInfo } from "library/hooks/types";
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { CreditCard } from "../CreditCard/CreditCard";
import { Separator } from "../Separator/Separator";
import { Cancel } from "./Cancel";
import { CancelLink } from "./components/CancelLink";
import {
  employmentStatusOptions,
  occupationOptions,
  relationshipStatusOptions,
  residentialStatusOptions,
} from "./options";
import { ActionType, ScreenProps } from "./types";

export const AboutYourself = ({ state, dispatch }: ScreenProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const navigate = useNavigate();
  const accountInfo = state.accountInfo as AccountInfo;
  const initialValues = {
    personalInformation: state.personalInformation,
    residentialAddress: state.residentialAddress,
    employmentDetails: state.employmentDetails,
  };
  const onSubmit = ({ values, errors }) => {
    if (Object.keys(errors).length === 0) {
      dispatch({
        type: ActionType.ABOUT_YOURSELF_SUBMITTED,
        personalInformation: values.personalInformation,
        residentialAddress: values.residentialAddress,
        employmentDetails: values.employmentDetails,
      });
    }
  };

  return (
    <Form initialValues={initialValues} onSubmit={onSubmit}>
      {({ state: { values } }) => (
        <>
          <Stack gap="8">
            <Container bg="grey.t05" padding="4">
              <CreditCard
                cardName={accountInfo.cardName}
                cardEndsWith={accountInfo.cardEndsWith}
              />
            </Container>
            <Stack gap="4">
              <Text>
                <strong>Step 1 of 3</strong>
              </Text>
              <Text as="h3" textStyle="heading4">
                Tell us a little more about yourself
              </Text>
            </Stack>
            <Separator />
            <Text as="h4" textStyle="heading5" color="primary.blue.t100">
              Personal information
            </Text>
            <Container width="100%" width-sm="400">
              <RadioGroup
                name="personalInformation.relationshipStatus"
                label="Relationship status"
                options={relationshipStatusOptions}
              />
            </Container>
            <Container width="100%" width-sm="400">
              <Input
                name="personalInformation.numberOfDependants"
                label="Number of dependants"
                variant="numeric"
                helpText="e.g. anyone other than your partner that resides at your residential address and is financially dependent on you"
              />
            </Container>
            <Separator />
            <Text as="h4" textStyle="heading5" color="primary.blue.t100">
              Residential address
            </Text>
            <Container width="100%" width-sm="400">
              <Select
                name="residentialAddress.residentialStatus"
                label="Residential status"
                options={residentialStatusOptions}
              />
            </Container>
            <Container width="100%" width-sm="400">
              <Input
                name="residentialAddress.mortgageOrRent"
                label="Mortgage or rent"
                maxLength="6"
                variant="numeric"
                prefix="$"
                suffix="per month"
                helpText="E.g. loans or payment (including board/rent) related to your current residential address, please include investment property repayments if applicable."
              />
            </Container>
            <Container width="100%" width-sm="400">
              <TimeSpan
                name="residentialAddress.timeAtCurrentAddress"
                label="Time at current address"
              />
            </Container>
            <Separator />
            <Text as="h4" textStyle="heading5" color="primary.blue.t100">
              Employment details
            </Text>
            <Container width="100%" width-sm="400">
              <Select
                name="employmentDetails.employmentStatus"
                label="Employment status"
                options={employmentStatusOptions}
              />
            </Container>
            <Container width="100%" width-sm="400">
              <Select
                name="employmentDetails.occupation"
                label="Occupation"
                options={occupationOptions}
              />
            </Container>
            <Container width="100%" width-sm="400">
              <TimeSpan
                name="employmentDetails.timeWithCurrentEmployer"
                label="Time with current employer"
              />
            </Container>
            <Container width="100%" width-sm="400">
              <FormActions>
                <FormActions.Primary>
                  <Button type="submit">Next</Button>
                </FormActions.Primary>
                <FormActions.Secondary>
                  <Button
                    variant="secondary"
                    onClick={() => {
                      dispatch({
                        type: ActionType.ABOUT_YOURSELF_BACK_CLICKED,
                        personalInformation: values.personalInformation,
                        residentialAddress: values.residentialAddress,
                        employmentDetails: values.employmentDetails,
                      });
                    }}
                  >
                    Back
                  </Button>
                </FormActions.Secondary>
              </FormActions>
            </Container>
            <CancelLink onClick={() => setIsModalOpen(true)} />
          </Stack>
          <Cancel
            isOpen={isModalOpen}
            onClose={() => setIsModalOpen(false)}
            onConfirm={() => {
              navigate("/manage-account");
            }}
          />
        </>
      )}
    </Form>
  );
};
